import { useState, useEffect, useRef } from "react";
import {
  type ButtonStates,
  type Joystick,
  type ButtonKey,
  type MouseSimulationHandlers,
  BUTTON_MAPPING,
  VIRTUAL_GAMEPAD_INDEX,
  DEADZONE,
} from "../constants/gamepad";

const MENU_HOLD_DURATION_MS = 1500;
interface UseVirtualGamepadInputProps extends MouseSimulationHandlers {
  onStateChange?: (state: {
    buttons: ButtonStates;
    joystick: Joystick;
  }) => void;
  sendMessage?: (message: string) => void;
  onToggleMenu?: () => void;
  isActive?: boolean;
}

const noop = () => undefined;
const alwaysFalse = () => false;

export const useVirtualGamepadInput = ({
  onStateChange,
  sendMessage,
  onToggleMenu,
  isActive = false,
  shouldSimulateMouseEvents = alwaysFalse,
  simulateMouseMovement = noop,
  simulateMouseEvent = noop,
  stopMouseSimulation = noop,
}: UseVirtualGamepadInputProps) => {
  const [buttonStates, setButtonStates] = useState<ButtonStates>({});
  const [leftJoystick, setLeftJoystick] = useState<Joystick>({
    x: 0,
    y: 0,
  });
  const [rightJoystick, setRightJoystick] = useState<Joystick>({
    x: 0,
    y: 0,
  });
  const [isLeftJoystickActive, setIsLeftJoystickActive] =
    useState<boolean>(false);
  const [isRightJoystickActive, setIsRightJoystickActive] =
    useState<boolean>(false);
  const activeTouchRef = useRef<number | null>(null);
  const rightActiveTouchRef = useRef<number | null>(null);
  const leftJoystickOuterRef = useRef<HTMLDivElement | null>(null);
  const rightJoystickOuterRef = useRef<HTMLDivElement | null>(null);
  // Cached for the duration of a drag — the on-screen joystick container
  // doesn't move/resize mid-gesture, so avoid a forced-layout read every touchmove.
  const leftJoystickRectRef = useRef<DOMRect | null>(null);
  const rightJoystickRectRef = useRef<DOMRect | null>(null);
  const previousButtonStates = useRef<ButtonStates>({});
  const previousLeftJoystick = useRef<Joystick>({ x: 0, y: 0 });
  const previousRightJoystick = useRef<Joystick>({ x: 0, y: 0 });
  const isConnected = useRef<boolean>(false);
  const lastPointerState = useRef<boolean | null>(null);
  const menuHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearMenuHoldTimer = (): void => {
    if (menuHoldTimerRef.current) {
      clearTimeout(menuHoldTimerRef.current);
      menuHoldTimerRef.current = null;
    }
  };

  // Connection and messaging logic
  useEffect(() => {
    if (sendMessage && isActive && !isConnected.current) {
      isConnected.current = true;
      const dualSenseId = btoa(
        "DualSense Wireless Controller (STANDARD GAMEPAD Vendor: 054c Product: 0ce6)",
      );
      sendMessage(`js,c,${VIRTUAL_GAMEPAD_INDEX},${dualSenseId},4,18`);
    } else if (sendMessage && !isActive && isConnected.current) {
      isConnected.current = false;
      sendMessage(`js,d,${VIRTUAL_GAMEPAD_INDEX}`);
      setButtonStates({});
      setLeftJoystick({ x: 0, y: 0 });
      setRightJoystick({ x: 0, y: 0 });
      previousButtonStates.current = {};
      previousLeftJoystick.current = { x: 0, y: 0 };
      previousRightJoystick.current = { x: 0, y: 0 };
      lastPointerState.current = null;
      clearMenuHoldTimer();
    }
  }, [sendMessage, isActive]);

  useEffect(() => clearMenuHoldTimer, []);

  // Show remote pointer when shouldSimulateMouseEvents()
  useEffect(() => {
    if (!sendMessage || !isActive) return;

    const currentPointerState = shouldSimulateMouseEvents();

    if (lastPointerState.current !== currentPointerState) {
      lastPointerState.current = currentPointerState;
      const message = currentPointerState ? "p,1" : "p,0"; // 1 - Show, 0 - Hide
      sendMessage(message);
    }
  }, [sendMessage, isActive, shouldSimulateMouseEvents]);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({ buttons: buttonStates, joystick: leftJoystick });
    }

    if (isActive) {
      // Button changes
      const useMouseSimulation = shouldSimulateMouseEvents();

      Object.keys(BUTTON_MAPPING).forEach((key) => {
        const buttonKey = key as ButtonKey;

        const currentState = buttonStates[buttonKey] || false;
        const previousState = previousButtonStates.current[buttonKey] || false;

        if (currentState !== previousState) {
          const buttonIndex = BUTTON_MAPPING[buttonKey];
          const value = currentState ? 1 : 0;

          sendMessage?.(
            `js,b,${VIRTUAL_GAMEPAD_INDEX},${buttonIndex},${value}`,
          );

          if (buttonKey === "home" || buttonKey === "start") {
            if (value) {
              clearMenuHoldTimer();
              menuHoldTimerRef.current = setTimeout(() => {
                menuHoldTimerRef.current = null;
                onToggleMenu?.();
              }, MENU_HOLD_DURATION_MS);
            } else {
              // Released before the hold completed — cancel, don't toggle.
              clearMenuHoldTimer();
            }
          }
        }
      });

      // Left joystick changes (axes 0,1)
      const leftRoundedX = Math.round(leftJoystick.x * 1000) / 1000;
      const leftRoundedY = Math.round(leftJoystick.y * 1000) / 1000;
      const leftPrevRoundedX =
        Math.round(previousLeftJoystick.current.x * 1000) / 1000;
      const leftPrevRoundedY =
        Math.round(previousLeftJoystick.current.y * 1000) / 1000;

      if (
        leftRoundedX !== leftPrevRoundedX ||
        leftRoundedY !== leftPrevRoundedY
      ) {
        // Don't forward left joystick as gamepad axis while simulating mouse
        if (!useMouseSimulation) {
          sendMessage?.(`js,a,${VIRTUAL_GAMEPAD_INDEX},0,${leftRoundedX}`);
          sendMessage?.(`js,a,${VIRTUAL_GAMEPAD_INDEX},1,${leftRoundedY}`);
        }

        if (useMouseSimulation && isLeftJoystickActive) {
          // Only update mouse position when joystick is actively being used
          // This keeps the cursor still when joystick is released and resets to (0,0)
          simulateMouseMovement(leftRoundedX, leftRoundedY);
        }
      }

      // Right joystick changes (axes 2,3)
      const rightRoundedX = Math.round(rightJoystick.x * 1000) / 1000;
      const rightRoundedY = Math.round(rightJoystick.y * 1000) / 1000;
      const rightPrevRoundedX =
        Math.round(previousRightJoystick.current.x * 1000) / 1000;
      const rightPrevRoundedY =
        Math.round(previousRightJoystick.current.y * 1000) / 1000;

      if (
        rightRoundedX !== rightPrevRoundedX ||
        rightRoundedY !== rightPrevRoundedY
      ) {
        sendMessage?.(`js,a,${VIRTUAL_GAMEPAD_INDEX},2,${rightRoundedX}`);
        sendMessage?.(`js,a,${VIRTUAL_GAMEPAD_INDEX},3,${rightRoundedY}`);
      }

      previousButtonStates.current = { ...buttonStates };
      previousLeftJoystick.current = { ...leftJoystick };
      previousRightJoystick.current = { ...rightJoystick };
    }
  }, [
    buttonStates,
    leftJoystick,
    rightJoystick,
    onStateChange,
    sendMessage,
    isActive,
    shouldSimulateMouseEvents,
    isLeftJoystickActive,
  ]);

  // Event handlers
  const handleButtonPress = (button: ButtonKey): void => {
    setButtonStates((prev) => ({ ...prev, [button]: true }));
  };

  const handleButtonRelease = (button: ButtonKey): void => {
    setButtonStates((prev) => ({ ...prev, [button]: false }));
  };

  const handleButtonTouchStart = (
    e: React.TouchEvent,
    button: ButtonKey,
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    if (
      shouldSimulateMouseEvents() &&
      !["start", "home", "select"].includes(button)
    ) {
      const mouseButton =
        button === "a" ? "left" : button === "b" ? "right" : null;
      if (mouseButton) simulateMouseEvent("mousedown", mouseButton);
      return;
    }

    handleButtonPress(button);
  };

  const handleButtonTouchEnd = (
    _e: React.TouchEvent,
    button: ButtonKey,
  ): void => {
    if (
      shouldSimulateMouseEvents() &&
      !["start", "home", "select"].includes(button)
    ) {
      const mouseButton =
        button === "a" ? "left" : button === "b" ? "right" : null;
      if (mouseButton) simulateMouseEvent("mouseup", mouseButton);
      return;
    }

    handleButtonRelease(button);
  };

  const handleButtonMouseDown = (
    _: React.MouseEvent,
    button: ButtonKey,
  ): void => {
    if ("ontouchstart" in window) return;
    handleButtonPress(button);
  };

  const handleButtonMouseUp = (
    _: React.MouseEvent,
    button: ButtonKey,
  ): void => {
    if ("ontouchstart" in window) return;
    handleButtonRelease(button);
  };

  // LeftJoystick stick logic
  const handleLeftJoystickMove = (clientX: number, clientY: number): void => {
    if (!leftJoystickRectRef.current) {
      if (!leftJoystickOuterRef.current) return;
      leftJoystickRectRef.current =
        leftJoystickOuterRef.current.getBoundingClientRect();
    }

    const rect = leftJoystickRectRef.current;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Convert from screen pixels to container-relative pixels
    let deltaX = clientX - centerX;
    let deltaY = clientY - centerY;

    // Keep movement within circular boundary
    const maxDistance = rect.width / 2 - 25;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    if (distance > maxDistance) {
      const angle = Math.atan2(deltaY, deltaX);
      deltaX = Math.cos(angle) * maxDistance;
      deltaY = Math.sin(angle) * maxDistance;
    }

    // Convert to standard -1 to +1 gamepad range
    let x = deltaX / maxDistance;
    let y = deltaY / maxDistance;

    // Ignore tiny movements near center
    if (Math.abs(x) < DEADZONE) x = 0;
    if (Math.abs(y) < DEADZONE) y = 0;

    setLeftJoystick({ x, y });
  };

  const resetLeftJoystick = (): void => {
    setIsLeftJoystickActive(false);
    activeTouchRef.current = null;
    leftJoystickRectRef.current = null;
    setLeftJoystick({ x: 0, y: 0 });
    stopMouseSimulation();
  };

  const handleLeftJoystickTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
  ): void => {
    const touch = e.changedTouches[e.changedTouches.length - 1];
    activeTouchRef.current = touch.identifier;
    setIsLeftJoystickActive(true);
    handleLeftJoystickMove(touch.clientX, touch.clientY);
  };

  const handleLeftJoystickTouchMove = (
    e: React.TouchEvent<HTMLDivElement>,
  ): void => {
    if (activeTouchRef.current === null) return;
    e.preventDefault();
    e.stopPropagation();

    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i];
      if (touch.identifier === activeTouchRef.current) {
        handleLeftJoystickMove(touch.clientX, touch.clientY);
        break;
      }
    }
  };

  const handleLeftJoystickTouchEnd = (
    e: React.TouchEvent<HTMLDivElement>,
  ): void => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === activeTouchRef.current) {
        resetLeftJoystick();
        break;
      }
    }
  };

  const handleLeftJoystickMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if ("ontouchstart" in window) return;

    setIsLeftJoystickActive(true);
    handleLeftJoystickMove(e.clientX, e.clientY);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (isLeftJoystickActive) {
        handleLeftJoystickMove(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = (): void => {
      if (isLeftJoystickActive) {
        resetLeftJoystick();
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isLeftJoystickActive]);

  // Right joystick logic
  const handleRightJoystickMove = (clientX: number, clientY: number): void => {
    if (!rightJoystickRectRef.current) {
      if (!rightJoystickOuterRef.current) return;
      rightJoystickRectRef.current =
        rightJoystickOuterRef.current.getBoundingClientRect();
    }

    const rect = rightJoystickRectRef.current;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Convert from screen pixels to container-relative pixels
    let deltaX = clientX - centerX;
    let deltaY = clientY - centerY;

    // Keep movement within circular boundary
    const maxDistance = rect.width / 2 - 25;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    if (distance > maxDistance) {
      const angle = Math.atan2(deltaY, deltaX);
      deltaX = Math.cos(angle) * maxDistance;
      deltaY = Math.sin(angle) * maxDistance;
    }

    // Convert to standard -1 to +1 gamepad range
    let x = deltaX / maxDistance;
    let y = deltaY / maxDistance;

    // Ignore tiny movements near center
    if (Math.abs(x) < DEADZONE) x = 0;
    if (Math.abs(y) < DEADZONE) y = 0;

    setRightJoystick({ x, y });
  };

  const resetRightJoystick = (): void => {
    setIsRightJoystickActive(false);
    rightActiveTouchRef.current = null;
    rightJoystickRectRef.current = null;
    setRightJoystick({ x: 0, y: 0 });
  };

  const handleRightJoystickTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
  ): void => {
    const touch = e.changedTouches[e.changedTouches.length - 1];
    rightActiveTouchRef.current = touch.identifier;
    setIsRightJoystickActive(true);
    handleRightJoystickMove(touch.clientX, touch.clientY);
  };

  const handleRightJoystickTouchMove = (
    e: React.TouchEvent<HTMLDivElement>,
  ): void => {
    if (rightActiveTouchRef.current === null) return;
    e.preventDefault();
    e.stopPropagation();

    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i];
      if (touch.identifier === rightActiveTouchRef.current) {
        handleRightJoystickMove(touch.clientX, touch.clientY);
        break;
      }
    }
  };

  const handleRightJoystickTouchEnd = (
    e: React.TouchEvent<HTMLDivElement>,
  ): void => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === rightActiveTouchRef.current) {
        resetRightJoystick();
        break;
      }
    }
  };

  const handleRightJoystickMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if ("ontouchstart" in window) return;
    setIsRightJoystickActive(true);
    handleRightJoystickMove(e.clientX, e.clientY);
  };

  // Add right joystick mouse events
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (isRightJoystickActive) {
        handleRightJoystickMove(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = (): void => {
      if (isRightJoystickActive) {
        resetRightJoystick();
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isRightJoystickActive]);

  const leftJoystickTransform = (): string => {
    if (
      !leftJoystickRectRef.current ||
      (leftJoystick.x === 0 && leftJoystick.y === 0)
    ) {
      return "translate(-50%, -50%)";
    }
    const rect = leftJoystickRectRef.current;
    const maxDistance = rect.width / 2 - 25;
    const deltaX = leftJoystick.x * maxDistance;
    const deltaY = leftJoystick.y * maxDistance;
    return `translate(calc(-50% + ${deltaX}px), calc(-50% + ${deltaY}px))`;
  };

  const rightJoystickTransform = (): string => {
    if (
      !rightJoystickRectRef.current ||
      (rightJoystick.x === 0 && rightJoystick.y === 0)
    ) {
      return "translate(-50%, -50%)";
    }
    const rect = rightJoystickRectRef.current;
    const maxDistance = rect.width / 2 - 25;
    const deltaX = rightJoystick.x * maxDistance;
    const deltaY = rightJoystick.y * maxDistance;
    return `translate(calc(-50% + ${deltaX}px), calc(-50% + ${deltaY}px))`;
  };

  return {
    buttonStates,
    leftJoystick,
    rightJoystick,
    isLeftJoystickActive,
    isRightJoystickActive,
    leftJoystickOuterRef,
    rightJoystickOuterRef,
    handleButtonTouchStart,
    handleButtonTouchEnd,
    handleButtonMouseDown,
    handleButtonMouseUp,
    handleLeftJoystickTouchStart,
    handleLeftJoystickTouchMove,
    handleLeftJoystickTouchEnd,
    handleLeftJoystickMouseDown,
    handleRightJoystickTouchStart,
    handleRightJoystickTouchMove,
    handleRightJoystickTouchEnd,
    handleRightJoystickMouseDown,
    leftJoystickTransform,
    rightJoystickTransform,
  };
};
